import { SITE } from '@cardengine/shared';
import { linkProps } from '../router.js';

/**
 * The privacy notice.
 *
 * Written to describe what the code actually does, not what a template says a
 * privacy policy should say. Every claim here is checkable against the source:
 * the retention figures are `LOBBY_IDLE_TIMEOUT_MS` and `CHAT_HISTORY_LIMIT`,
 * the storage keys are the ones in `store.ts`, and the metrics description is
 * the model in `metrics.ts`.
 *
 * If any of that changes, this page has to change with it.
 */
export function Privacy() {
  return (
    <main className="page page--prose">
      <header className="prose__head">
        <p className="prose__eyebrow">Privacy</p>
        <h1 className="prose__title">What {SITE.name} knows about you</h1>
        <p className="prose__lead">
          Not much, and not for long. There are no accounts, no tracking, and nothing is shared
          with anyone else. This page says exactly what happens to what you do type in.
        </p>
      </header>

      <section className="prose__section">
        <h2>The short version</h2>
        <ul className="prose__list">
          <li>No account, no email address, no password — ever.</li>
          <li>No analytics, no advertising, and no third-party scripts of any kind.</li>
          <li>Nothing you type is kept once your table closes.</li>
          <li>Nothing is sold, shared or sent anywhere else.</li>
        </ul>
      </section>

      <section className="prose__section">
        <h2>What is collected while you play</h2>
        <p>
          <strong>The name you choose.</strong> It is shown to everyone else at your table. It can
          be anything — if you would rather not use your own, the <em>Use a nickname</em> button
          beside the name box picks one for you.
        </p>
        <p>
          <strong>Chat messages.</strong> The last fifty messages at a table are kept so that
          somebody who reconnects can see what they missed.
        </p>
        <p>
          <strong>Your cards and the state of the game</strong>, which is what makes it possible to
          reconnect to a game in progress rather than losing your hand.
        </p>
        <p>
          All three live only as long as the table does. A table is deleted thirty minutes after
          it goes quiet, or as soon as the last person leaves — and with it, the name, the chat and
          the cards. Leaving a table is how you erase yourself; there is no account to delete
          because there was never one to make.
        </p>
      </section>

      <section className="prose__section">
        <h2>What is stored in your browser</h2>
        <p>
          Five things, all set by this site and never read by anyone else: a token that keeps your
          seat if your connection drops, the name you last used, your chosen card backs, whether
          sound is on, and whether playable cards are highlighted.
        </p>
        <p>
          The seat token is what makes reconnecting work, and the other four are settings you chose
          yourself — none of it is used to follow you around, here or anywhere else. That is why
          there is no cookie banner: there is nothing to consent to. Clearing your browser storage
          removes all of it.
        </p>
      </section>

      <section className="prose__section">
        <h2>Visitor numbers</h2>
        <p>
          Counts only: how many tables were made, how many games were dealt, and how many people
          were playing at once. No names, no addresses, no chat, and nothing recording what any
          individual did or when they were here.
        </p>
        <p>
          Counting how many different visitors there have been needs some way to recognise a return
          visit, so a one-way scrambled code is kept for each — with <em>no time recorded beside
          it</em>. That means the total can say how many, and cannot say who or when. It is
          deliberately built so the question is unanswerable rather than merely unasked.
        </p>
      </section>

      <section className="prose__section">
        <h2>Addresses and logs</h2>
        <p>
          The server does not keep a record of who visited. Your network address is used in the
          moment to deliver the page and to slow down anyone trying to guess their way into the
          admin page, and is not written down for either. If something goes wrong, the error the
          server records may include the address it happened for.
        </p>
      </section>

      {/* Deliberately says nothing about who runs the site or where from. An
          email is a working contact route without publishing either. */}
      <section className="prose__section">
        <h2>How to ask about any of this</h2>
        <p>
          To ask what is held about you, to have something removed, or to complain, write to{' '}
          <a href="mailto:hello@freehouse.games">hello@freehouse.games</a>.
        </p>
        <p>
          If you are not satisfied with the answer, you can complain to the data protection
          authority for the country you live in — that is your own country&rsquo;s regulator, not
          one you need to look up here.
        </p>
      </section>

      <section className="prose__section">
        <h2>Children</h2>
        <p>
          There is nothing here that is unsuitable for children, and nothing that asks a visitor
          for personal details. Since anyone can type anything into a table&rsquo;s chat, a table
          is only as private as the code you share — so share codes with people you know.
        </p>
      </section>

      <div className="prose__cta">
        <a className="btn btn--primary" {...linkProps('/')}>
          Back to {SITE.name}
        </a>
      </div>
    </main>
  );
}
